var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo } from 'react';
import StorefrontIcon from '@mui/icons-material/Storefront';
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest';
import { brandEndpointIds } from 'root/src/shared/brand/brand.constants';
import brandSettingsSelector from 'root/src/client/logic/form/selectors/brandSettingsSelector';
import { useAppDispatch, useAppSelector } from 'root/src/client/logic/helpers/useStore';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { getReadableError } from 'root/src/client/v2/common/helpers/getReadableError';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { usePublisherSettingsForm, buildBrandSettingsPayload, PublisherSettingsFields, } from 'root/src/client/v2/profile/features/ProfileSettingsDialog/publisherSettingsForm';
// Maps a SET_BRAND_SETTINGS error envelope code to a readable toast line (client validation + the
// square crop already prevent most of these; this is the safety net).
const ERROR_MESSAGES = {
    NotURL: 'Enter a valid website URL',
    BadImageSize: 'Logo must be at least 130px',
    IncorrectRatio: 'Logo must be square',
    BadImage: 'There was a problem with the logo image',
    IncorrectTwitter: 'Enter a valid Twitter / X handle',
};
// Redesign of the legacy after-login PublisherSettingsModal (src/client/web/modal/PublisherSettingsModal.js),
// shown to brand users on first login when they have no publisher settings yet. Non-closeable (no close
// button): the brand must complete name + website to proceed. Reuses the shared publisher settings form
// (same fields/schema as the settings dialog). `onSaved` resolves the app-init await that gates the rest
// of onboarding (see RedesignPublisherSettingsModalBridge).
export const RedesignPublisherSettingsModal = memo(({ onSaved }) => {
    var _a;
    const dispatch = useAppDispatch();
    const { hide } = useRedesignModal();
    const current = useAppSelector(brandSettingsSelector);
    const { loading, success, error, run } = useAsyncAction();
    const { register, handleSubmit, setValue, watch, formState: { errors } } = usePublisherSettingsForm(current);
    const brandLogoUrl = (_a = watch('brandLogoUrl')) !== null && _a !== void 0 ? _a : '';
    const onSubmit = handleSubmit((values) => {
        void run(() => __awaiter(void 0, void 0, void 0, function* () {
            const payload = buildBrandSettingsPayload(values);
            const result = yield dispatch(apiRequest(brandEndpointIds.SET_BRAND_SETTINGS, payload));
            if (result === null || result === void 0 ? void 0 : result.error) {
                const { code } = result.error;
                throw new Error((code && ERROR_MESSAGES[code]) || 'Could not save publisher settings');
            }
        }), {
            onSuccess: () => { onSaved === null || onSaved === void 0 ? void 0 : onSaved(); hide(); },
            toast: {
                loading: 'Saving publisher settings...',
                success: 'Publisher settings saved',
                error: caught => getReadableError(caught, 'Could not save publisher settings'),
            },
        });
    }, () => {
        toast.error('Check the form', { description: 'Fix the highlighted fields.' });
    });
    return (<RedesignModalShell className="sm:max-w-xl">
			<div className="flex w-full flex-col items-center gap-2 text-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-neon-purple/30 bg-neon-purple/10">
					<StorefrontIcon sx={{ fontSize: 24 }} className="text-neon-purple"/>
				</div>
				<h2 className="font-display text-xl font-bold text-foreground">Set up your publisher profile</h2>
				<p className="text-sm text-muted-foreground">
					Tell creators and viewers who you are. You can change this anytime in Publisher Settings.
				</p>
			</div>
			<form id="redesign-publisher-settings-form" onSubmit={onSubmit} className="w-full space-y-3 text-left">
				<PublisherSettingsFields register={register} errors={errors} brandLogoUrl={brandLogoUrl} onLogoUploaded={url => setValue('brandLogoUrl', url, { shouldDirty: true, shouldValidate: true })}/>
			</form>
			<Button type="submit" form="redesign-publisher-settings-form" variant="brand" loading={loading} success={success} error={error} className="w-full rounded-lg text-sm font-semibold">
				Save and continue
			</Button>
		</RedesignModalShell>);
});
